import React, { useState, useEffect, useContext } from 'react';
import { GripVertical, Youtube, Edit3, Check, Plus, Trash2, X, Link, EyeIcon, TrendingUpIcon } from 'lucide-react';
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { extractYoutubeId, isProduction } from './utils';
import LoginWithSocial from './login/LoginWithSocial';
import { updateUser } from './ApiHandlers';
import { contexto } from "./CustomProvider";
import Dolar from './Dolar';
import './Principal.scss';
import LogOut from './login/LogOut';
import LocationPermissionModal from './LocationPermissionModal';
import { Notify } from 'notiflix';
import VideoPlayer from './VideoPlayer';

export default function App() {
    const { usuarioContexto, usuariosActivosCantidad, tandasPublicitarias, thisDeviceData, canalesDisponiblesTodos, fetchChannels, userLocation, updateUserLocation } = useContext(contexto);
    const [searchParams] = useSearchParams();
    const [gridChannels, setGridChannels] = useState([]);
    const [columns, setColumns] = useState(() => {
        const idealSlotWidth = 450;
        const screenWidth = window.innerWidth;
        return Math.max(1, Math.floor(screenWidth / idealSlotWidth));
    });
    const [editing, setEditing] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedFrom, setDraggedFrom] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [mainMonitorEnabled, setMainMonitorEnabled] = useState(false);
    const [mainMonitorChannel, setMainMonitorChannel] = useState(null);
    const [mainMonitorOriginalIndex, setMainMonitorOriginalIndex] = useState(null);
    const [hoveredSlot, setHoveredSlot] = useState(null);
    const [selectedCasas, setSelectedCasas] = useState(['Oficial', 'Blue', 'Blue/Oficial', 'BTC/USD', 'ETH/USD']);
    const [showAddStreamModal, setShowAddStreamModal] = useState(false);
    const [streamUrl, setStreamUrl] = useState('');
    const [streamData, setStreamData] = useState(null);
    const [urlError, setUrlError] = useState(null);
    const [customVideos, setCustomVideos] = useState([]);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [pendingChannel, setPendingChannel] = useState(null);
    // const [attentionChannel, setAttentionChannel] = useState(null);

    const adSource = searchParams.get('adsrc');
    const navigate = useNavigate();

    if (adSource) {
        if (adSource === "bn") {
            localStorage.setItem("CAJABOBA-adsource", "textual.banner-28_01_26");
            localStorage.setItem("CAJABOBA-adsource-date", JSON.stringify(new Date()));
            navigate("/", { replace: true });
        }
        if (adSource === "pup") {
            localStorage.setItem("CAJABOBA-adsource", "textual.popup-28_01_26");
            localStorage.setItem("CAJABOBA-adsource-date", JSON.stringify(new Date()));
            navigate("/", { replace: true });
        }
    }


    // Cargar configuración desde thisDeviceData cuando esté disponible (SOLO UNA VEZ)
    useEffect(() => {
        if (!canalesDisponiblesTodos.length) return;
        if (configLoaded) return;

        const deviceConfig = usuarioContexto?.dispositivos?.find(
            d => d.deviceId === thisDeviceData?.deviceId
        )?.config;

        if (deviceConfig) {
            setColumns(deviceConfig.columns || 2);
            setMainMonitorEnabled(deviceConfig.mainMonitorEnabled || false);
            setSelectedCasas(deviceConfig.selectedCasas || ['Oficial', 'Blue', 'Bolsa', 'CCL']);

            if (deviceConfig.gridChannelIds && Array.isArray(deviceConfig.gridChannelIds)) {
                const resolvedChannels = deviceConfig.gridChannelIds
                    .map(channelId => canalesDisponiblesTodos.find(ch => ch.id === channelId))
                    .filter(Boolean);
                setGridChannels(resolvedChannels);
            }

            if (deviceConfig.customVideos && Array.isArray(deviceConfig.customVideos)) {
                setCustomVideos(deviceConfig.customVideos);
            }
        } else {
            // NO HAY CONFIGURACIÓN - Usuario nuevo o sin login
            const defaultVideo = {
                id: 'default-welcome',
                videoId: '2fcytq-pVGc', // Reemplaza con tu videoId
                channelTitle: 'La Caja Boba',
                title: 'Bienvenido a La Caja Boba',
                type: 'youtube',
                color: '#FF6B6B',
                isCustomVideo: false
            };

            setGridChannels([defaultVideo]);
        }

        setConfigLoaded(true);
    }, [thisDeviceData?.deviceId, canalesDisponiblesTodos.length]);

    // Auto-buscar stream cuando cambia la URL
    useEffect(() => {
        if (streamUrl) {
            handleSearchStream();
        }
    }, [streamUrl]);

    // Guardar configuración automáticamente
    const saveConfig = async (newGridChannels, newColumns, newMainMonitorEnabled, newSelectedCasas, newCustomVideos) => {
        if (!usuarioContexto?.uid || !thisDeviceData?.deviceId) {
            console.log('No se puede guardar: falta usuario o dispositivo');
            Notify.success("Recuerde iniciar sesión para guardar los cambios!", {
                showOnlyTheLastOne: true
            });
            return;
        }

        if (isSaving) {
            console.warn('⏳ Ya hay un guardado en progreso, ignorando...');
            return;
        }

        setIsSaving(true);

        const gridChannelIds = newGridChannels.map(ch => ch.id);

        const cleanCustomVideos = (newCustomVideos !== undefined ? newCustomVideos : customVideos).map(video => ({
            id: video.id,
            videoId: video.videoId,
            channelTitle: video.channelTitle,
            title: video.title,
            type: video.type,
            color: video.color,
            isCustomVideo: true
        }));

        const config = {
            gridChannelIds,
            columns: newColumns !== undefined ? newColumns : columns,
            mainMonitorEnabled: newMainMonitorEnabled !== undefined ? newMainMonitorEnabled : mainMonitorEnabled,
            selectedCasas: newSelectedCasas !== undefined ? newSelectedCasas : selectedCasas,
            customVideos: cleanCustomVideos
        };

        const dispositivos = usuarioContexto.dispositivos || [];
        const deviceIndex = dispositivos.findIndex(d => d.deviceId === thisDeviceData.deviceId);

        let updatedDispositivos;
        if (deviceIndex !== -1) {
            updatedDispositivos = dispositivos.map((device, idx) => {
                if (idx === deviceIndex) {
                    return { ...device, config };
                }
                return device;
            });
        } else {
            updatedDispositivos = [
                ...dispositivos,
                {
                    deviceId: thisDeviceData.deviceId,
                    screenWidth: thisDeviceData.screenWidth,
                    screenHeight: thisDeviceData.screenHeight,
                    userAgent: thisDeviceData.userAgent,
                    isMobile: thisDeviceData.isMobile,
                    browser: thisDeviceData.browser,
                    os: thisDeviceData.os,
                    runningMode: thisDeviceData.runningMode,
                    platform: thisDeviceData.platform,
                    language: thisDeviceData.language,
                    lastUpdated: thisDeviceData.lastUpdated,
                    registeredAt: thisDeviceData.registeredAt,
                    config
                }
            ];
        }

        try {
            updateUser(usuarioContexto.uid, {
                dispositivos: updatedDispositivos
            });
            console.log('💾 Configuración guardada en backend');
        } catch (err) {
            console.error('❌ Error al guardar config:', err);
        } finally {
            setTimeout(() => {
                setIsSaving(false);
            }, 1000);
        }
    };

    const availableChannels = [
        ...canalesDisponiblesTodos,
        ...customVideos.map(video => ({
            ...video,
            isCustomVideo: true,
            type: 'youtube'
        }))
    ].filter(channel => !gridChannels.find(gc => gc.id === channel.id))
        .filter(channel => {
            if (!searchTerm.trim()) return true;
            const search = searchTerm.toLowerCase();
            return channel.channelTitle?.toLowerCase().includes(search) ||
                channel.title?.toLowerCase().includes(search);
        });

    const handleDragStart = (e, channel, from) => {
        e.stopPropagation();
        setDraggedItem(channel);
        setDraggedFrom(from);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', '');
    };

    const handleDragEnd = (e) => {
        e.stopPropagation();
        setDraggedItem(null);
        setDraggedFrom(null);
        setDragOverIndex(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnterSlot = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        if (editing) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeaveSlot = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
            setDragOverIndex(null);
        }
    };

    const handleDropOnSlot = (e, dropIndex, isEmpty) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedItem || !editing) return;

        let newGridChannels = [...gridChannels];

        if (draggedFrom === 'available') {
            if (isEmpty) {
                newGridChannels.splice(dropIndex, 0, draggedItem);
            } else {
                newGridChannels.splice(dropIndex, 0, draggedItem);
            }
        } else if (draggedFrom === 'grid') {
            const draggedIndex = newGridChannels.findIndex(c => c.id === draggedItem.id);
            if (draggedIndex !== -1) {
                newGridChannels.splice(draggedIndex, 1);
                const adjustedDropIndex = dropIndex > draggedIndex ? dropIndex - 1 : dropIndex;
                newGridChannels.splice(adjustedDropIndex, 0, draggedItem);
            }
        }

        setGridChannels(newGridChannels);
        saveConfig(newGridChannels, columns);
        setDraggedItem(null);
        setDraggedFrom(null);
        setDragOverIndex(null);
    };

    const handleDropOnSidebar = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedItem || draggedFrom !== 'grid' || !editing) return;

        const newGridChannels = gridChannels.filter(c => c.id !== draggedItem.id);
        setGridChannels(newGridChannels);
        saveConfig(newGridChannels, columns);
        setDraggedItem(null);
        setDraggedFrom(null);
        setDragOverIndex(null);
    };

    const removeFromGrid = (channelId) => {
        const newGridChannels = gridChannels.filter(c => c.id !== channelId);
        setGridChannels(newGridChannels);
        saveConfig(newGridChannels, columns);
    };

    // Modificado para manejar canales que requieren ubicación
    const addToGrid = (channel) => {
        if (channel.requiresLocation && !userLocation) {
            setPendingChannel(channel);
            setShowLocationModal(true);
            return;
        }

        const newGridChannels = [...gridChannels, channel];
        setGridChannels(newGridChannels);
        saveConfig(newGridChannels, columns);
    };

    const handleLocationGranted = (location) => {
        console.log({ location })
        updateUserLocation(location);
        setShowLocationModal(false);

        if (pendingChannel) {
            const newGridChannels = [...gridChannels, pendingChannel];
            setGridChannels(newGridChannels);
            saveConfig(newGridChannels, columns);
            setPendingChannel(null);
        }
    };

    const handleColumnsChange = (newColumns) => {
        const columnsValue = parseInt(newColumns, 10);
        if (isNaN(columnsValue) || columnsValue < 1) return;

        const finalColumns = Math.min(columnsValue, 6);

        setColumns(finalColumns);
        saveConfig(gridChannels, finalColumns);
    };

    const toggleMainMonitor = () => {
        const newValue = !mainMonitorEnabled;
        setMainMonitorEnabled(newValue);
        if (!newValue) {
            setMainMonitorChannel(null);
            setMainMonitorOriginalIndex(null);
        }
        saveConfig(gridChannels, columns, newValue);
    };

    const moveToMainMonitor = (channel, index, e) => {
        console.log({ channel })
        if (!editing && mainMonitorEnabled) {
            e.stopPropagation();

            // Guardar la posición original
            setMainMonitorOriginalIndex(index);

            // Remover el canal de la grilla temporalmente
            const newGridChannels = gridChannels.filter(ch => ch.id !== channel.id);
            setGridChannels(newGridChannels);

            // Establecer el canal en el monitor grande
            setMainMonitorChannel(channel);
        }
    };

    const clearMainMonitor = () => {
        if (mainMonitorChannel && mainMonitorOriginalIndex !== null) {
            // Reinsertar el canal en su posición original
            const newGridChannels = [...gridChannels];
            newGridChannels.splice(mainMonitorOriginalIndex, 0, mainMonitorChannel);
            setGridChannels(newGridChannels);
        }

        setMainMonitorChannel(null);
        setMainMonitorOriginalIndex(null);
    };

    const toggleCasa = (casaName) => {
        let newSelectedCasas;
        if (selectedCasas.includes(casaName)) {
            newSelectedCasas = selectedCasas.filter(c => c !== casaName);
        } else {
            newSelectedCasas = [...selectedCasas, casaName];
        }
        setSelectedCasas(newSelectedCasas);
        saveConfig(gridChannels, columns, mainMonitorEnabled, newSelectedCasas);
    };

    const handleSearchStream = () => {
        setUrlError(null);
        setStreamData(null);

        if (!streamUrl.trim()) {
            setUrlError('Por favor ingresa una URL');
            return;
        }

        const videoId = extractYoutubeId(streamUrl);

        if (!videoId) {
            setUrlError('URL inválida. Debe ser un link de YouTube válido');
            return;
        }

        const existsInChannels = canalesDisponiblesTodos.find(ch => ch.videoId === videoId);
        const existsInCustom = customVideos.find(v => v.videoId === videoId);

        if (existsInChannels || existsInCustom) {
            setUrlError('Este video ya está en tu lista');
            return;
        }

        setStreamData({ loading: true });

        fetch(`${isProduction() ? window.location.origin : 'http://localhost:8080'}/api/channel/${videoId}`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ addedBy: usuarioContexto.uid })
        })
            .then(rtta => rtta.json())
            .then(response => {
                setStreamData(response);
            })
            .catch(err => {
                console.error('Error al buscar stream:', err);
                setUrlError('Error al buscar el video');
                setStreamData(null);
            });
    };

    const handleSaveStream = () => {
        if (!streamData || urlError || streamData.loading) return;

        const newCustomVideo = {
            id: `custom-${Date.now()}`,
            videoId: streamData.videoId || extractYoutubeId(streamUrl),
            channelTitle: streamData.channelTitle || 'Video Personalizado',
            title: streamData.title || '',
            type: 'youtube',
            color: '#' + Math.floor(Math.random() * 16777215).toString(16),
            isCustomVideo: true,
        };

        const newCustomVideos = [...customVideos, newCustomVideo];
        setCustomVideos(newCustomVideos);

        const newGridChannels = [newCustomVideo, ...gridChannels];
        setGridChannels(newGridChannels);

        saveConfig(newGridChannels, columns, mainMonitorEnabled, selectedCasas, newCustomVideos);

        setTimeout(() => {
            fetchChannels();
        }, 1000);

        setStreamData(null);
        setStreamUrl('');
        setShowAddStreamModal(false);
        setUrlError(null);
    };

    const removeCustomVideo = (videoId) => {
        const newCustomVideos = customVideos.filter(v => v.id !== videoId);
        setCustomVideos(newCustomVideos);

        const newGridChannels = gridChannels.filter(ch => ch.id !== videoId);
        setGridChannels(newGridChannels);

        saveConfig(newGridChannels, columns, mainMonitorEnabled, selectedCasas, newCustomVideos);
    };

    const totalSlots = columns * Math.max(2, Math.ceil(gridChannels.length / columns));
    const slots = [];
    for (let i = 0; i < totalSlots; i++) {
        slots.push(gridChannels[i] || null);
    }

    if (canalesDisponiblesTodos.length === 0) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div>Cargando canales disponibles...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="header">
                <div className="header-brand">
                    <div className="logo-wrapper">
                        <img
                            src="/logo.png"
                            alt="La Caja Boba"
                            className="header-logo"
                        />
                        <span className="header-counter">{usuariosActivosCantidad}</span>
                    </div>
                    <h1 className="header-title">La Caja Boba 3.0</h1>
                </div>

                {editing ? (
                    <div className="header-top">
                        <div className="column-buttons">
                            <label className="column-input-label">
                                Cantidad de Columnas:
                                <input
                                    type="number"
                                    min="1"
                                    max="8"
                                    value={columns}
                                    onChange={(e) => handleColumnsChange(e.target.value)}
                                    className="column-input"
                                />
                            </label>
                            <button
                                onClick={toggleMainMonitor}
                                className={`btn-monitor-toggle ${mainMonitorEnabled ? 'active' : ''}`}
                            >
                                <Youtube size={16} />
                                Monitor Grande {mainMonitorEnabled ? 'ON' : 'OFF'}
                            </button>
                        </div>
                        <button
                            className="btn-edit active"
                            onClick={() => {
                                // Si hay canal en monitor grande, cerrarlo antes de salir del modo edición
                                if (mainMonitorChannel) {
                                    clearMainMonitor();
                                }
                                setEditing(!editing);
                            }}
                        >
                            <Check size={18} />
                            Finalizar
                        </button>
                    </div>
                ) : (
                    <div className="header-actions">
                        <LoginWithSocial show={JSON.stringify(usuarioContexto) === "{}"} />

                        {(usuarioContexto?.uid === "LJ6xawWJheUWBbChukhyen6gBCf1" || usuarioContexto?.uid === "5dxKTVDluFY6mRo3krktdwBf5eP2" || usuarioContexto?.uid === "EuFKs5ABkCM9AGUWMTo98R40ivo1" || usuarioContexto?.uid === "3IzkCxr4PaO5A5JlZpnAtmuHCxk2") &&
                            <NavLink
                                className="btn-admin"
                                to="/admin"
                            >Admin Panel</NavLink>}

                        <button
                            className="btn-add-stream"
                            onClick={() => setShowAddStreamModal(true)}
                        >
                            <Link size={18} />
                            Agregar Stream
                        </button>
                        <button
                            className="btn-edit"
                            onClick={() => {
                                // Si hay canal en monitor grande, cerrarlo antes de entrar al modo edición
                                if (mainMonitorChannel) {
                                    clearMainMonitor();
                                }
                                setEditing(!editing);
                            }}
                        >
                            <Edit3 size={18} />
                            Editar
                        </button>
                    </div>
                )}
            </div>

            <Dolar
                selectedCasas={selectedCasas}
                onToggleCasa={toggleCasa}
                editing={editing}
            />

            <div className="main-content">
                {editing && (
                    <div
                        className="sidebar"
                        onDragOver={handleDragOver}
                        onDrop={handleDropOnSidebar}
                    >
                        <h3 className="sidebar-title">
                            <GripVertical size={20} />
                            Canales Disponibles
                        </h3>

                        <div className="sidebar-search">
                            <input
                                type="text"
                                placeholder="Buscar canales..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button
                                    className="search-clear"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="channel-list">
                            {availableChannels.length === 0 ? (
                                <div style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '16px' }}>
                                    {searchTerm ? 'No se encontraron canales' : 'Todos los canales están en uso'}
                                </div>
                            ) : (
                                availableChannels.map(channel => (
                                    <div
                                        key={channel.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, channel, 'available')}
                                        onDragEnd={handleDragEnd}
                                        className="available-channel"
                                    >
                                        <GripVertical size={16} color="#64748b" />
                                        <div
                                            className="available-channel-dot"
                                            style={{ background: channel.color }}
                                        />
                                        <span className="available-channel-name">
                                            {channel.channelTitle}
                                            {channel.isCustomVideo && (
                                                <span className="available-channel-badge">MANUAL</span>
                                            )}
                                            {channel.isCustomChannel && (
                                                <span className="available-channel-badge">ESPECIAL</span>
                                            )}
                                            {channel.live?.isLive && (
                                                <div className="available-channel-live-info">
                                                    <EyeIcon size={14} className="eye-icon" />
                                                    <span className="viewers-count">
                                                        {channel.live.viewers.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}

                                            {/* {<div className="available-channel-live-info">
                                                <TrendingUpIcon size={14} className="eye-icon" />
                                                <span className="viewers-count">
                                                    {parseInt(channel.popularity * 100)} %
                                                </span>
                                            </div>} */}
                                        </span>

                                        {channel.isCustomVideo ? (
                                            <button
                                                className="add-button remove"
                                                onClick={() => removeCustomVideo(channel.id)}
                                            >
                                                <X size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                className="add-button"
                                                onClick={() => addToGrid(channel)}
                                            >
                                                <Plus size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div className="grid-container">
                    {mainMonitorEnabled && !editing && mainMonitorChannel && (
                        <div className="main-monitor">
                            <VideoPlayer channel={mainMonitorChannel} autoplay={true} mute={false} />
                            <button
                                className="main-monitor-close"
                                onClick={clearMainMonitor}
                            >
                                <X size={20} />
                                Cerrar
                            </button>
                        </div>
                    )}

                    <div
                        className="grid"
                        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                    >
                        {slots.map((channel, index) => {
                            const isHovered = dragOverIndex === index && editing;
                            const isEmpty = !channel;

                            const esTanda = tandasPublicitarias?.[channel?.id];
                            const tipoClase = esTanda ? 'tanda' : esTanda === false ? 'programa' : '';

                            return (
                                <div
                                    key={`slot-${index}`}
                                    className={`grid-slot ${isEmpty ? 'empty' : ''} ${isHovered ? 'hovered' : ''} ${tipoClase}`}
                                    onDragOver={handleDragOver}
                                    onDragEnter={(e) => handleDragEnterSlot(e, index)}
                                    onDragLeave={handleDragLeaveSlot}
                                    onDrop={(e) => handleDropOnSlot(e, index, isEmpty)}
                                    draggable={editing && !isEmpty}
                                    onDragStart={(e) => !isEmpty && handleDragStart(e, channel, 'grid')}
                                    onDragEnd={handleDragEnd}
                                    onMouseEnter={() => !editing && !isEmpty && setHoveredSlot(index)}
                                    onMouseLeave={() => setHoveredSlot(null)}
                                >
                                    {isEmpty ? (
                                        <div className={`grid-slot-empty-content ${isHovered ? 'hovered' : ''}`}>
                                            {editing ? (isHovered ? 'Suelta aquí' : 'Arrastra o +') : ''}
                                        </div>
                                    ) : (
                                        <>
                                            {!editing && (
                                                <div
                                                    className="video-overlay"
                                                    style={{ background: channel.color }}
                                                />
                                            )}

                                            {/* {!editing && <div
                                                onMouseEnter={() => { setAttentionChannel(index) }}
                                                onMouseLeave={() => { setAttentionChannel(-1) }}
                                                style={{ width: '100%', height: '100%' }}
                                            >
                                                <VideoPlayer
                                                    channel={channel}
                                                    autoplay={index < columns}
                                                    mute={attentionChannel !== index}
                                                />
                                            </div>} */}

                                            {!editing &&
                                                <VideoPlayer
                                                    channel={channel}
                                                    autoplay={index < columns}
                                                    mute={index > 0}
                                                />}

                                            {!editing && mainMonitorEnabled && (
                                                <button
                                                    className={`send-to-monitor ${hoveredSlot === index ? 'visible' : ''}`}
                                                    onClick={(e) => moveToMainMonitor(channel, index, e)}
                                                >
                                                    <Youtube size={16} />
                                                    Ver en grande
                                                </button>
                                            )}

                                            {editing && (
                                                <>
                                                    <div className="drag-handle">
                                                        <GripVertical size={20} color="white" />
                                                    </div>
                                                    <button
                                                        className="remove-button"
                                                        onClick={() => removeFromGrid(channel.id)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                    <div style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(0,0,0,0.7)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <img
                                                            src={channel.type === 'youtube'
                                                                ? `https://img.youtube.com/vi/${channel.videoId}/maxresdefault.jpg`
                                                                : channel.type === 'twitch'
                                                                    ? `https://static-cdn.jtvnw.net/previews-ttv/live_user_${channel.channel}-440x248.jpg`
                                                                    : null
                                                            }
                                                            alt={channel.channelTitle}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                opacity: 0.4,
                                                                display: (channel.type === 'custom-iframe' || channel.type === 'custom-component') ? 'none' : 'block'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                        {(channel.type === 'custom-iframe' || channel.type === 'custom-component') && (
                                                            <div style={{
                                                                color: 'white',
                                                                fontSize: '18px',
                                                                fontWeight: '600',
                                                                textAlign: 'center'
                                                            }}>
                                                                {channel.channelTitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Modal de agregar stream manual */}
            {showAddStreamModal && (
                <div className="modal" onClick={() => setShowAddStreamModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-title">
                            <Link size={28} />
                            Agregar Stream Manual
                        </div>

                        <div>
                            <div className="modal-label">URL de YouTube *</div>
                            <input
                                type="text"
                                placeholder="https://www.youtube.com/watch?v=..."
                                value={streamUrl}
                                onChange={(e) => setStreamUrl(e.target.value)}
                                className="modal-input"
                            />
                            {urlError && <div className="modal-error">{urlError}</div>}
                            {streamData?.loading && (
                                <div className="modal-loading">Buscando video...</div>
                            )}
                        </div>

                        {streamData && !streamData.loading && (
                            <div className="modal-stream-preview">
                                <div className="preview-row">
                                    <Youtube size={18} />
                                    <span className="channel-title">{streamData.channelTitle}</span>
                                </div>
                                <div className="preview-row">
                                    <span className="video-title">{streamData.title}</span>
                                </div>
                                <div className="preview-row">
                                    {streamData.isLive ? (
                                        <span className="live-badge">EN VIVO</span>
                                    ) : (
                                        <span className="offline-badge">Offline</span>
                                    )}
                                    {streamData.concurrentViewers > 0 && (
                                        <div className="viewers">
                                            <EyeIcon size={16} />
                                            <span className="viewers-number">
                                                {streamData.concurrentViewers.toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="modal-buttons">
                            <button
                                className="modal-button-secondary"
                                onClick={() => {
                                    setStreamData(null);
                                    setShowAddStreamModal(false);
                                    setStreamUrl('');
                                    setUrlError(null);
                                }}
                            >
                                Cancelar
                            </button>

                            <button
                                disabled={!streamData || streamData.loading || urlError}
                                className="modal-button-primary"
                                onClick={handleSaveStream}
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de permisos de ubicación */}
            <LocationPermissionModal
                isOpen={showLocationModal}
                onClose={() => {
                    setShowLocationModal(false);
                    setPendingChannel(null);
                }}
                onLocationGranted={handleLocationGranted}
                hasPreviousLocation={!!thisDeviceData?.location}
                uid={usuarioContexto?.uid}
                deviceId={thisDeviceData?.deviceId}
            />

            <LogOut show={JSON.stringify(usuarioContexto) !== "{}"} />
        </div>
    );
}